// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  Warning,
  SvgIcon,
  NavLink
} from '../../'

import { decodeBase64 } from '../../../utils'

const QUERY_EMPLOYER_IDENTITIES = gql`
  query($employerUuid: UUID!, $divisionUuid: UUID) {
    identities(client_Division_Employer_Id: $employerUuid, client_Division_Id: $divisionUuid) {
      edges {
        node {
          id
          msisdnDefault
          waId
          tgId
        }
      }
    }
  }
`

const QUERY_EMPLOYERGROUP_IDENTITIES = gql`
  query($employerGroupUuid: UUID!) {
    identities(client_Division_Employer_Employergroup_Id: $employerGroupUuid) {
      edges {
        node {
          id
          msisdnDefault
          waId
          tgId
        }
      }
    }
  }
`


export const EmployeeStats = ({ employerId, divisionId, employerGroupId, className }) => {
  let query = null
  let variables = null

  if (employerId) {
    query = QUERY_EMPLOYER_IDENTITIES
    variables = {
      employerUuid: decodeBase64(employerId),
      divisionUuid: divisionId
        ? decodeBase64(divisionId)
        : undefined
    }
  }

  if (employerGroupId) {
    query = QUERY_EMPLOYERGROUP_IDENTITIES
    variables = {
      employerGroupUuid: decodeBase64(employerGroupId)
    }
  }

  return (
    <div className='slds-p-around_small'>
      <Query
        query={query}
        variables={variables}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Spinner size='small' className='slds-is-relative' />
          }

          if (error) {
            return <Warning>Failed to fetch required data</Warning>
          }

          const employees = data.identities.edges
          const totalNumberOfEmployees = employees.length

          if (totalNumberOfEmployees === 0) {
            return (
              <div className='slds-text-align_center'>
                <SvgIcon
                  variant='utility'
                  symbol='info'
                  colour='light'
                  size='small'
                  className='slds-m-right_small'
                />
                No employees are loaded.
              </div>
            )
          }

          const employeesWithValidCellphoneNumbers = employees.filter(({ node: employee }) => (
            employee.msisdnDefault !== null
          )).length
          const employeesRegisteredForWhatsAppMessages = employees.filter(({ node: employee }) => (
            employee.waId !== null
          )).length
          const employeesRegisteredForTelegramMessages = employees.filter(({ node: employee }) => (
            employee.tgId !== null
          )).length

          return (
            <div
              className={`slds-grid slds-text-color_weak u-font-size-larger ${className}`}
              style={{
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <div className='slds-m-right_medium u-font-size-larger'>
                Number of employees:
                <div className='u-font-weight-bolder u-font-size-larger' style={{ textAlign: 'center' }}>
                  {totalNumberOfEmployees > 0
                    ? <NavLink to={`/employer/${employerId ? employerId : employerGroupId}/employees`}>
                        {totalNumberOfEmployees}
                      </NavLink>
                    : 0
                  }
                </div>
              </div>
              <div className='slds-p-left_medium' style={{ borderLeft: '3px solid rgb(90, 86, 251)' }}>
                {`${employeesWithValidCellphoneNumbers} have valid cellphone numbers`}
                <br />
                {employeesRegisteredForTelegramMessages > 0
                  ? <NavLink to={`/employer/${employerId ? employerId : employerGroupId}/employees?channel=Telegram`}>
                      {employeesRegisteredForTelegramMessages}
                    </NavLink>
                  : 0
                } registered for Telegram messages
                <br />
                {employeesRegisteredForWhatsAppMessages
                  ? <NavLink to={`/employer/${employerId ? employerId : employerGroupId}/employees?channel=WhatsApp`}>
                      {employeesRegisteredForWhatsAppMessages}
                    </NavLink>
                  : 0
                } registered on the WhatsApp channel
              </div>
            </div>
          )
        }}
      </Query>
    </div>
  )
}

EmployeeStats.propTypes = {
  employerId: PropTypes.string,
  employerGroupId: PropTypes.string,
  divisionId: PropTypes.string,
  className: PropTypes.string
}

EmployeeStats.defaultProps = {
  className: ''
}



// WEBPACK FOOTER //
// ./src/components/messageplus/employee-stats/index.js